import cloneDeep from 'lodash/cloneDeep'

import { digObject } from '@domain-group/javascript-utils'

import type { TaskListItemModel } from '@models/taskListItem'

import useSelector from '@hooks/useSelector'
import useStatuses from '@hooks/useStatuses'
import { useTaskListItemForm } from '@hooks/useTaskListItem'

import { statusKeys } from '@functions/status'

type ToggleRelatedEntityParams = {
  data?: object,
  entityState: Partial<TaskListItemModel>,
  setEntityState: (params: any) => void,
  subjectId: number,
  subjectType: string,
}

export const toggleRelatedEntityAttributes = (params: ToggleRelatedEntityParams) => {
  const {
    data = {}, entityState, setEntityState, subjectId, subjectType,
  } = params

  const relatedEntitiesAttributes = cloneDeep(entityState.related_entities_attributes || [])

  const existingIndex = relatedEntitiesAttributes.findIndex((entity) => {
    const { subject_type } = entity
    return subjectType === subject_type
  })

  // Add to array
  if (subjectId && existingIndex === -1){
    const relatedEntity = {
      data: JSON.stringify(data),
      owner_id: entityState.id,
      owner_type: 'TaskListItem',
      subject_id: subjectId,
      subject_type: subjectType,
    }

    relatedEntitiesAttributes.push(relatedEntity)
  }

  // Remove if it has been cleared
  if (!subjectId && existingIndex !== -1){
    relatedEntitiesAttributes.splice(existingIndex, 1)
  }

  setEntityState({ related_entities_attributes: relatedEntitiesAttributes })

  return relatedEntitiesAttributes
}

const findRelatedEntityByType = (params: { entityState: any, subjectType: string }) => {
  const { entityState, subjectType } = params
  const relatedEntitiesAttributes = entityState.related_entities_attributes || []

  const relatedEntity = relatedEntitiesAttributes.find((entity: { subject_type: string }) => {
    const { subject_type } = entity
    return subjectType === subject_type
  })

  return relatedEntity || {}
}

function useCreateOrEditTaskListItemModal(taskListItem: Partial<TaskListItemModel>) {
  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceProducts } = entities

  const formPayload = useTaskListItemForm(taskListItem, { validateOn: 'change' })
  const { entityState, setEntityState } = formPayload

  const providerRelatedEntity = findRelatedEntityByType({ entityState, subjectType: 'Provider' })
  const serviceProductRelatedEntity = findRelatedEntityByType({ entityState, subjectType: 'ServiceProduct' })
  const templatePageRelatedEntity = findRelatedEntityByType({ entityState, subjectType: 'TemplatePage' })

  const serviceProduct = serviceProducts[serviceProductRelatedEntity?.subject_id] || {}
  const serviceProductTemplatePages = digObject(serviceProduct, 'options.template.pages', [])

  const statusesPayload = useStatuses({
    filters: {
      status_keys: statusKeys,
    },
  })

  const { filteredStatuses } = statusesPayload

  return {
    callbacks: {
      toggleRelatedEntityAttributes: (params: { subjectType: string, subjectId: number }) => (
        toggleRelatedEntityAttributes({
          entityState,
          setEntityState,
          ...params,
        })
      ),
    },
    formPayload,
    providerRelatedEntity,
    serviceProduct,
    serviceProductRelatedEntity,
    serviceProductTemplatePages,
    statuses: filteredStatuses,
    templatePageRelatedEntity,
  }
}

export default useCreateOrEditTaskListItemModal
